import React, { useRef } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import Box from '../../../components/Common/Box';
import ChartShareButton from '../../../components/Common/ChartShareButton';
import { AllocationItem } from '../usePortfolioAnalysis';
import { fcfa, pct } from '../portfolioFormat';

interface AllocationChartProps {
    title: string;
    data: AllocationItem[];
}

const COLORS = [
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#8dd1e1',
];

const AllocationChart: React.FC<AllocationChartProps> = ({ title, data }) => {
    const chartRef = useRef<HTMLDivElement>(null);

    return (
        <Box>
            <div style={{ padding: '20px', position: 'relative' }} ref={chartRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3
                        style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--text-heading)' }}
                    >
                        {title}
                    </h3>
                    <ChartShareButton
                        targetRef={chartRef}
                        fileName={`allocation_${title.toLowerCase().replace(/\s+/g, '_')}`}
                    />
                </div>

                <div style={{ height: '300px', width: '100%' }}>
                    <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                            <Pie
                                data={data}
                                cx='50%'
                                cy='50%'
                                innerRadius={60}
                                outerRadius={80}
                                fill='#8884d8'
                                paddingAngle={5}
                                dataKey='value'
                                nameKey='label'
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value: number, _nom: string, entree: { payload?: AllocationItem }) => [
                                    `${fcfa(value)} · ${pct(entree.payload?.percentage)}`,
                                    entree.payload?.label ?? '',
                                ]}
                                contentStyle={{
                                    borderRadius: '8px',
                                    border: 'none',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                }}
                            />
                            {/* Le pourcentage est dans la légende : au survol seul, il était invisible sur mobile. */}
                            <Legend
                                verticalAlign='bottom'
                                iconType='circle'
                                wrapperStyle={{ color: 'var(--text-main)', fontSize: '13px' }}
                                formatter={(libelle: string) =>
                                    `${libelle} · ${pct(data.find(d => d.label === libelle)?.percentage)}`}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Box>
    );
};

export default AllocationChart;
